<template>
    <login-layout
        eyebrow="Administración"
        :titulo="titulos[paso].titulo"
        :subtitulo="titulos[paso].subtitulo">

        <!-- Paso 1: usuario y contraseña -->
        <v-form v-if="paso === 'credenciales'" ref="form" v-model="formValid" @submit.prevent="login">
            <v-text-field
                class="campo"
                outlined
                prepend-inner-icon="person"
                type="text"
                autocomplete="username"
                :label="usuarioForm.usuario.label"
                :placeholder="usuarioForm.usuario.placeholder"
                v-model="usuarioModel.usuario"
                :rules="usuarioForm.usuario.rules"
                @keyup.enter="login"
            />
            <v-text-field
                class="campo"
                outlined
                prepend-inner-icon="key"
                autocomplete="current-password"
                :type="hidePassword ? 'password' : 'text'"
                :append-icon="hidePassword ? 'visibility_off' : 'visibility'"
                :label="usuarioForm.password.label"
                :placeholder="usuarioForm.password.placeholder"
                v-model="usuarioModel.password"
                :rules="usuarioForm.password.rules"
                @click:append="hidePassword = !hidePassword"
                @keyup.enter="login"
            />

            <v-btn class="boton-principal mt-2" depressed :loading="loading" @click="login">
                Iniciar sesión
                <v-icon right>arrow_forward</v-icon>
            </v-btn>

            <div class="text-center mt-4">
                <a class="enlace-secundario" href="#" @click.prevent="irARecuperar">¿Olvidó su contraseña?</a>
            </div>
        </v-form>

        <!-- Paso 2: segundo factor -->
        <div v-else-if="paso === 'verificacion'">
            <!-- Mixto: elegir con qué verificar -->
            <v-btn-toggle v-if="factores.length > 1" v-model="factor" mandatory dense class="selector-factor mb-5" @change="cambiarFactor">
                <v-btn value="app" small>
                    <v-icon left small>phonelink_lock</v-icon> Aplicación
                </v-btn>
                <v-btn value="correo" small>
                    <v-icon left small>mail</v-icon> Correo
                </v-btn>
            </v-btn-toggle>

            <!-- Aplicación autenticadora sin configurar: enlazarla -->
            <div v-if="factor === 'app' && !appConfigurada" class="enlazar-app mb-4">
                <p class="text-body-2 mb-3">
                    1. Instale <b>Google Authenticator</b> o <b>Microsoft Authenticator</b> en su teléfono.<br>
                    2. Escanee este código con la aplicación.<br>
                    3. Escriba abajo el código de 6 dígitos que le muestre.
                </p>
                <div class="text-center">
                    <v-progress-circular v-if="!configuracionApp" indeterminate color="primary" class="my-6"/>
                    <template v-else>
                        <img :src="configuracionApp.qr" alt="Código QR para la aplicación autenticadora" class="enlazar-app__qr">
                        <div class="text-caption mt-1">
                            ¿No puede escanear? Clave manual:
                            <code class="enlazar-app__clave">{{ claveLegible }}</code>
                        </div>
                    </template>
                </div>
            </div>

            <p v-else-if="factor === 'app'" class="text-body-2 mb-4">
                Abra su aplicación autenticadora y escriba el código de 6 dígitos de <b>Certificados INFOP</b>.
            </p>

            <p v-else class="text-body-2 mb-4">
                <template v-if="codigoEnviado">Enviamos un código a <b>{{ correoEnmascarado }}</b>. Vence en 10 minutos.</template>
                <template v-else>Le enviaremos un código de 6 dígitos a <b>{{ correoEnmascarado || 'su correo' }}</b>.</template>
            </p>

            <v-text-field
                ref="campoCodigo"
                class="campo campo-codigo"
                outlined
                prepend-inner-icon="pin"
                label="Código de verificación"
                placeholder="000000"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                v-model="codigo"
                :disabled="factor === 'correo' && !codigoEnviado"
                @keyup.enter="verificar"
            />

            <v-btn class="boton-principal mt-1" depressed :loading="loading"
                   :disabled="codigo.length !== 6" @click="verificar">
                Verificar e ingresar
                <v-icon right>verified_user</v-icon>
            </v-btn>

            <div class="d-flex justify-space-between mt-4">
                <a class="enlace-secundario" href="#" @click.prevent="reiniciar">
                    <v-icon small>arrow_back</v-icon> Volver
                </a>
                <a v-if="factor === 'correo'" class="enlace-secundario" href="#"
                   :class="{'enlace-secundario--inactivo': esperaReenvio > 0 || enviando}"
                   @click.prevent="enviarCodigo">
                    {{ esperaReenvio > 0 ? `Reenviar en ${esperaReenvio} s` : (codigoEnviado ? 'Reenviar código' : 'Enviar código') }}
                </a>
            </div>
        </div>

        <!-- Recuperar contraseña -->
        <v-form v-else ref="formRecuperar" v-model="recuperarValido" @submit.prevent>
            <template v-if="!recuperar.solicitado">
                <v-text-field
                    class="campo" outlined prepend-inner-icon="person"
                    label="Usuario" autocomplete="username"
                    v-model="usuarioModel.usuario"
                    :rules="usuarioForm.usuario.rules"
                    @keyup.enter="solicitarRecuperacion"
                />
                <v-btn class="boton-principal mt-1" depressed :loading="loading" @click="solicitarRecuperacion">
                    Enviar código a mi correo
                    <v-icon right>mail</v-icon>
                </v-btn>
            </template>

            <template v-else>
                <v-text-field
                    class="campo campo-codigo" outlined prepend-inner-icon="pin"
                    label="Código recibido por correo" placeholder="000000"
                    inputmode="numeric" maxlength="6" autocomplete="one-time-code"
                    v-model="recuperar.codigo"
                    :rules="[(v) => /^\d{6}$/.test(v || '') || 'Escriba los 6 dígitos']"
                />
                <v-text-field
                    class="campo" outlined prepend-inner-icon="key"
                    label="Contraseña nueva" autocomplete="new-password"
                    :type="hidePassword ? 'password' : 'text'"
                    :append-icon="hidePassword ? 'visibility_off' : 'visibility'"
                    v-model="recuperar.password"
                    :rules="reglasPassword"
                    @click:append="hidePassword = !hidePassword"
                />
                <v-text-field
                    class="campo" outlined prepend-inner-icon="key"
                    label="Confirmar contraseña" autocomplete="new-password"
                    :type="hidePassword ? 'password' : 'text'"
                    v-model="recuperar.confirmar"
                    :rules="[(v) => v === recuperar.password || 'Las contraseñas deben coincidir']"
                    @keyup.enter="restablecer"
                />
                <v-btn class="boton-principal mt-1" depressed :loading="loading" @click="restablecer">
                    Guardar contraseña
                    <v-icon right>check</v-icon>
                </v-btn>
            </template>

            <div class="text-center mt-4">
                <a class="enlace-secundario" href="#" @click.prevent="reiniciar">
                    <v-icon small>arrow_back</v-icon> Volver al inicio de sesión
                </a>
            </div>
        </v-form>

        <template #pie>
            <router-link :to="{name: 'LoginCertificados'}">
                <v-icon>workspace_premium</v-icon> Portal de participantes
            </router-link>
        </template>
    </login-layout>
</template>

<script>
import Ls from "@/services/ls";
import LoginLayout from "@/components/Login/LoginLayout.vue";
import {reglasPassword} from "@/services/password";

export default {
    name: 'LoginAdmin',
    components: {LoginLayout},
    data() {
        return {
            loading: false,
            hidePassword: true,
            formValid: true,
            recuperarValido: true,
            // credenciales | verificacion | recuperar
            paso: "credenciales",
            titulos: {
                credenciales: {titulo: "Acceso del personal", subtitulo: "Gestión de cursos, importaciones, plantillas y lotes de impresión."},
                verificacion: {titulo: "Verifique su identidad", subtitulo: "Su cuenta está protegida con un segundo paso."},
                recuperar: {titulo: "Recuperar contraseña", subtitulo: "Le enviaremos un código al correo registrado en su cuenta."},
            },
            // Segundo factor
            desafio: null,
            factores: [],
            factor: "app",
            appConfigurada: false,
            configuracionApp: null,
            correoEnmascarado: null,
            codigo: "",
            codigoEnviado: false,
            enviando: false,
            esperaReenvio: 0,
            temporizadorReenvio: null,
            // Recuperación
            recuperar: {solicitado: false, codigo: "", password: "", confirmar: ""},
            reglasPassword,
            usuarioModel: {
                usuario: null,
                password: null
            },
            usuarioForm: {
                usuario: {
                    label: 'Usuario',
                    placeholder: 'Usuario',
                    rules: [(v) => !!v || 'Campo requerido'],
                },
                password: {
                    label: 'Contraseña',
                    placeholder: 'Contraseña',
                    rules: [(v) => !!v || 'Campo requerido'],
                },
            },
        }
    },
    computed: {
        claveLegible() {
            return this.configuracionApp ? this.configuracionApp.secreto.replace(/(.{4})/g, "$1 ").trim() : "";
        },
    },
    methods: {
        goLoginCertificados() {
            this.$router.push({name: "LoginCertificados"});
        },
        login() {
            if (!this.$refs.form.validate()) {
                this.$toast.warning("Complete la información requerida.");
                return;
            }

            this.loading = true;

            let params = {
                usuario: this.usuarioModel.usuario,
                password: this.usuarioModel.password,
            };

            window.axios.post('/login', params).then(response => {
                const data = response.data;

                if (data.status == "active") {
                    return this.entrar(data);
                }

                if (data.status == "segundo_factor") {
                    return this.iniciarVerificacion(data);
                }

                this.$toast.error(data.message || "No se pudo iniciar sesión.");
            })
                .catch(error => this.errorConexion(error))
                .finally(() => {
                    this.loading = false;
                })
        },
        async entrar(respuesta) {
            let data = respuesta.data;
            Ls.set('token', respuesta.token);
            Ls.set('tipo_persona', data.cat_tipo_persona_id);
            Ls.set('nombre', data.nombre);
            Ls.set('apellido', data.apellido);

            // Contraseña temporal: primero hay que cambiarla.
            if (respuesta.debe_cambiar_password) {
                await this.$router.push({name: 'CambiarPassword'});
                return;
            }

            await this.$router.push({name: 'Inicio'});
        },
        // ----- Segundo factor -----
        iniciarVerificacion(data) {
            this.desafio = data.desafio;
            this.factores = data.factores;
            this.appConfigurada = data.app_configurada;
            this.correoEnmascarado = data.correo;
            this.codigo = "";
            this.codigoEnviado = false;
            this.configuracionApp = null;

            // Con ambos métodos se propone la aplicación si ya está enlazada; si no, el correo.
            this.factor = this.factores.length > 1
                ? (this.appConfigurada || !data.correo_disponible ? "app" : "correo")
                : this.factores[0];

            this.paso = "verificacion";
            this.cambiarFactor();
        },
        cambiarFactor() {
            this.codigo = "";

            if (this.factor === "app" && !this.appConfigurada && !this.configuracionApp) {
                this.cargarConfiguracionApp();
            }

            if (this.factor === "correo" && !this.codigoEnviado) {
                this.enviarCodigo();
            }

            this.$nextTick(() => this.$refs.campoCodigo && this.$refs.campoCodigo.focus());
        },
        cargarConfiguracionApp() {
            window.axios.post('/login/configurar_app', {desafio: this.desafio}).then(response => {
                if (response.data.success) {
                    this.configuracionApp = response.data.data;
                } else {
                    this.manejarRechazo(response.data);
                }
            }).catch(error => this.errorConexion(error));
        },
        enviarCodigo() {
            if (this.esperaReenvio > 0 || this.enviando) return;

            this.enviando = true;

            window.axios.post('/login/enviar_codigo', {desafio: this.desafio}).then(response => {
                if (response.data.success) {
                    this.codigoEnviado = true;
                    this.$toast.success(response.data.message);
                    this.iniciarEsperaReenvio();
                    this.$nextTick(() => this.$refs.campoCodigo && this.$refs.campoCodigo.focus());
                } else {
                    this.manejarRechazo(response.data);
                }
            })
                .catch(error => this.errorConexion(error))
                .finally(() => {
                    this.enviando = false;
                });
        },
        iniciarEsperaReenvio() {
            clearInterval(this.temporizadorReenvio);
            this.esperaReenvio = 60;
            this.temporizadorReenvio = setInterval(() => {
                this.esperaReenvio--;
                if (this.esperaReenvio <= 0) clearInterval(this.temporizadorReenvio);
            }, 1000);
        },
        verificar() {
            if (this.codigo.length !== 6 || this.loading) return;

            this.loading = true;
            const params = {desafio: this.desafio, factor: this.factor, codigo: this.codigo};

            window.axios.post('/login/verificar', params).then(response => {
                const data = response.data;

                if (data.status == "active") {
                    return this.entrar(data);
                }

                this.codigo = "";
                this.manejarRechazo(data);
            })
                .catch(error => this.errorConexion(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        manejarRechazo(data) {
            this.$toast.error(data.message || "No se pudo verificar.");

            if (data.status == "expirado") {
                this.reiniciar();
            }
        },
        reiniciar() {
            clearInterval(this.temporizadorReenvio);
            this.esperaReenvio = 0;
            this.paso = "credenciales";
            this.desafio = null;
            this.codigo = "";
            this.usuarioModel.password = null;
            this.recuperar = {solicitado: false, codigo: "", password: "", confirmar: ""};
        },
        // ----- Recuperación de contraseña -----
        irARecuperar() {
            this.recuperar = {solicitado: false, codigo: "", password: "", confirmar: ""};
            this.paso = "recuperar";
        },
        solicitarRecuperacion() {
            if (!this.usuarioModel.usuario) {
                this.$toast.warning("Escriba su usuario.");
                return;
            }

            this.loading = true;

            window.axios.post('/password/solicitar', {usuario: this.usuarioModel.usuario}).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.recuperar.solicitado = true;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.errorConexion(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        restablecer() {
            if (!this.$refs.formRecuperar.validate()) return;

            this.loading = true;
            const params = {
                usuario: this.usuarioModel.usuario,
                codigo: this.recuperar.codigo,
                password: this.recuperar.password,
                password_confirmation: this.recuperar.confirmar,
            };

            window.axios.post('/password/restablecer', params).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.reiniciar();
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.errorConexion(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        errorConexion(error) {
            console.log(error);
            const respuesta = error.response;

            if (respuesta && respuesta.status === 429) {
                this.$toast.error("Demasiados intentos. Espere un minuto e intente de nuevo.");
            } else if (respuesta && respuesta.data && respuesta.data.data && typeof respuesta.data.data === "object") {
                this.$toast.error(Object.values(respuesta.data.data).flat().join(" "));
            } else {
                this.$toast.error("No se pudo conectar");
            }
        },
    },
    created() {
        if (Ls.get("token")) {
            this.$router.push({name: 'Inicio'});
        }
    },
    beforeDestroy() {
        clearInterval(this.temporizadorReenvio);
    }
}
</script>

<style scoped>
.enlace-secundario {
    font-size: 0.85rem;
    color: #203D89;
    text-decoration: none;
    font-weight: 600;
}

.enlace-secundario:hover {
    text-decoration: underline;
}

.enlace-secundario--inactivo {
    color: #8a93ab;
    pointer-events: none;
}

.selector-factor {
    width: 100%;
}

.selector-factor .v-btn {
    flex: 1;
    text-transform: none;
}

.enlazar-app__qr {
    width: 190px;
    height: 190px;
    border: 1px solid #e3e7f1;
    border-radius: 12px;
    padding: 6px;
    background: #fff;
}

.enlazar-app__clave {
    display: inline-block;
    font-size: 0.8rem;
    letter-spacing: 0.06em;
    word-break: break-all;
    background: #f2f4fa;
    color: #1b2340;
    padding: 2px 6px;
    border-radius: 4px;
}

.campo-codigo >>> input {
    font-size: 1.35rem;
    letter-spacing: 0.4em;
    font-variant-numeric: tabular-nums;
}
</style>
